import { JSX, useEffect, useRef, useState } from "react";
import { Col, ColorPicker, ColorPickerProps, Divider, Form, Row, Switch, Tag, Upload } from "antd";
import { DeleteOutlined, DownOutlined, PercentageOutlined, PlusOutlined } from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import styled from "styled-components";
import { useQuery } from "react-query";
import Text from "../../../components/Text/Text";
import Button from "../../../components/Button/Button";
import Input from "../../../components/Input/Input";
import TextArea from "../../../components/Input/TextArea";
import CardSection from "../../../components/Card/CardSection";
import ImgCrop from "../../../components/ImgCrop/ImgCrop";
import color from "../../../resource/color";
import { ShopTierCompanyConfig, TierDefinition } from "../../ShopTier/config";
import { ConfigTierBenefit } from "../../ShopTier/mockData";
import { getTierProgramDetail } from "../../../datasource/TierProgramDatasource";
import TierCardPreview from "../TierCardPreview";
import IconUploadBox from "../IconUploadBox";
import ClosingReviewNotice from "../ClosingReviewNotice";
import { EditableTierBenefit, TierBenefitItem } from "../types";
import { createImageMaxSizeValidator } from "../imageValidation";
import { MAX_REBATE_PERCENT, toRebatePercent } from "../rebatePercent";
import { icon } from "../../../resource";
import image from "../../../resource/image";

export interface WorkflowCard {
  iconUrl?: string;
  iconPath?: string;
  File?: File;
  title: string;
  description: string;
}

export interface FaqItem {
  question: string;
  answer: string;
}

export interface RuleTopic {
  title: string;
  content: string;
}

export interface Step2Result {
  initialBannerPath: string | null;
  bannerUrl: string | null;
  bannerFile?: File;
  cards: WorkflowCard[];
  faqs: FaqItem[];
  rules: RuleTopic[];
  tiers: EditableTierBenefit[];
  cardImageOverrides: Record<string, string>;
  cardBackgroundOverrides: Record<string, string>;
  cardImageOverridesFile: Record<string, File>;
  cardBackgroundOverridesFile: Record<string, File>;
  colorOverrides: Record<string, string>;
  changedTierKeys: string[];
}

interface Props {
  tierProgramId?: string;
  mode: "create" | "edit";
  config: ShopTierCompanyConfig;
  sourceTiers: ConfigTierBenefit[];
  startFrom?: string;
  onNext: (result: Step2Result) => void;
  onBack: () => void;
  company: string;
  /** View-only: every field is disabled and add/delete/upload/next controls are hidden. */
  readOnly?: boolean;
}

function tierMinThreshold(subLabel: string): number {
  const match = subLabel.match(/[\d,]+(\.\d+)?/);
  return match ? parseFloat(match[0].replace(/,/g, "")) : 0;
}

function formatVolumeAmount(value: number, volumeUnit: string): string {
  return volumeUnit === "ล้านบาท" ? value.toFixed(1) : Math.round(value).toLocaleString("th-TH");
}

// HSB saturation floor, compared in whole percent (what the picker's S field shows).
const MIN_TIER_COLOR_SATURATION_PERCENT = 40;

type PickerColor = Parameters<NonNullable<ColorPickerProps["onChange"]>>[0];

// Review-queue threshold is a share of the next tier's target, so 0–100.
const MAX_QUEUE_THRESHOLD_PERCENT = 100;

// Keys that would let a number input hold a sign or an exponent (e.g. "-5", "1e3").
function blockSignAndExponent(e: React.KeyboardEvent<HTMLInputElement>) {
  if (e.key === "-" || e.key === "+" || e.key === "e" || e.key === "E") e.preventDefault();
}

/**
 * Parses a number input for a value capped at `max`. Returns null when the new text would go
 * over the cap, so the caller keeps the previous value — the extra keystroke simply doesn't land.
 */
function parseCappedNumber(raw: string, max: number): number | null {
  const n = Number(raw);
  if (Number.isNaN(n) || n < 0 || n > max) return null;
  return n;
}

function isBlankText(value?: string | null): boolean {
  return !value || value.trim() === "";
}

// ReactQuill leaves markup like "<p><br></p>" behind when the editor is emptied.
function isBlankHtml(value?: string | null): boolean {
  if (!value) return true;
  return value.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim() === "";
}

/** One missing/invalid field. `id` matches the `data-field-id` on the field's wrapper. */
interface FieldErrorItem {
  id: string;
  message: string;
  section?: "cards" | "faqs" | "rules";
  tierKey?: string;
}

function FieldError({ message }: { message?: string }): JSX.Element | null {
  if (!message) return null;
  return (
    <div style={{ marginTop: 4 }}>
      <Text fontFamily="Sarabun" fontSize={14} color="error">{message}</Text>
    </div>
  );
}

/**
 * Saturation in whole percent. A colour picked in this session is judged on the HSB the user
 * actually chose: converting to 8-bit hex and back shifts S by up to a few points (e.g. S 40 /
 * B 52 → #855050 → S 39.85), which made valid colours fail. Colours that only exist as hex
 * (from the API) fall back to the hex value.
 */
function saturationPercent(hex: string, picked?: PickerColor): number {
  const s = picked ? picked.toHsb().s : hexSaturation(hex);
  return Math.round(s * 100);
}

function hexSaturation(hex: string): number {
  const clean = hex.replace("#", "");
  if (clean.length < 6) return 1;
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  return max === 0 ? 0 : (max - min) / max;
}

const quillModules = {
  toolbar: [
    ["bold", "italic", "link"],
    [{ header: [false] }],
    [{ align: "" }, { align: "center" }, { align: "right" }],
    [{ list: "ordered" }, { list: "bullet" }],
  ],
};

const QuillContainer = styled.div<{ $hasError?: boolean }>`
  .ql-toolbar.ql-snow {
    border-radius: 4px 4px 0 0 !important;
  }
  .ql-container.ql-snow {
    border-radius: 0 0 4px 4px !important;
    font-family: Sarabun;
    ${(props) => (props.$hasError ? `border-color: ${color.error} !important;` : "")}
  }
  .ql-editor {
    min-height: 120px;
  }
  .ql-picker.ql-header .ql-picker-label::before,
  .ql-picker.ql-header .ql-picker-item::before {
    content: "ปกติ";
  }
`;

function ItemRow({ children, onDelete }: { children: React.ReactNode; onDelete?: () => void }) {
  return (
    <Row wrap={false} align="middle" gutter={8} style={{ marginBottom: 8 }}>
      <Col flex="auto">{children}</Col>
      {onDelete && <Col flex="none">
        <div
          onClick={onDelete}
          style={{
            width: 40,
            height: 40,
            borderRadius: 8,
            border: `1px solid ${color.error}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <DeleteOutlined style={{ color: color.error }} />
        </div>
      </Col>}
    </Row>
  );
}

function SectionHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div>
      <Text fontFamily="Sarabun" fontWeight={700} fontSize={16} color="Text2">
        {title}
      </Text>
      <div style={{ marginTop: 2 }}>
        <Text fontFamily="Sarabun" fontSize={13} color="Text3">
          {subtitle}
        </Text>
      </div>
    </div>
  );
}

function CountToggle({
  count,
  unit,
  expanded,
  onToggle,
}: {
  count: number;
  unit: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div onClick={onToggle} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
      <Text fontSize={13} color="Text3">
        {count} {unit}
      </Text>
      <DownOutlined
        style={{
          fontSize: 11,
          color: color.Text3,
          transform: expanded ? "rotate(180deg)" : "none",
          transition: "transform 0.15s ease",
        }}
      />
    </div>
  );
}

function Step2({ mode, config, sourceTiers, startFrom, onNext, onBack, tierProgramId, company, readOnly = false }: Props): JSX.Element {
  const [form] = Form.useForm();

  const isNewFromScratch = mode === "create" && startFrom !== "copy";

  const { data: tierProgramDetail } = useQuery(
    ["tier-program-detail", tierProgramId],
    async() => {
      const res  = await getTierProgramDetail(tierProgramId as string);
      return res;
    },
    { enabled: !!tierProgramId && !isNewFromScratch, refetchOnWindowFocus: false },
  );
  // react-query keeps a query's last-fetched data around even after `enabled` turns
  // false, so guard explicitly here too — "สร้างใหม่ทั้งหมด" must never see leftover data.
  const tierProgramData = isNewFromScratch ? undefined : tierProgramDetail?.responseData;
  // Only an existing program being edited is locked by its own year-end review;
  // a new program copied from it is not under review yet.
  const closingReview = mode === "edit" ? tierProgramData?.closingReview : undefined;
  const isClosingReviewOpen = Boolean(closingReview?.isOpen);
  const initialBannerPath: string | null = tierProgramData?.programImageUrl
  const initialBannerUrl: string | null =
    tierProgramData?.programImageLink || tierProgramData?.defaultProgramImageLink || null;
  const [bannerUrl, setBannerUrl] = useState<string | null>(initialBannerUrl);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const initialCards: WorkflowCard[] =
    tierProgramData?.howItWorks?.map((item: any) => ({
      title: item.title,
      description: item.detail,
      iconUrl: item.imageLink,
      iconPath: item.imageUrl,
    })) || [];
  const initialFaqs: FaqItem[] =
    tierProgramData?.faqs?.map((item: any) => ({
      question: item.title,
      answer: item.detail,
    })) || [];
  const initialRules: RuleTopic[] =
    tierProgramData?.termsAndConditions?.map((item: any) => ({
      title: item.title,
      content: item.detail,
    })) || [];

  // Fires once per actual data transition (not per re-render): compares a plain
  // signal instead of trusting `tierProgramData`'s object reference to stay stable,
  // so an unrelated re-render elsewhere on the page can never re-run this and
  // stomp a freshly uploaded icon / edited field back to the original API value.
  const contentHydrationRef = useRef<string | null>(null);
  useEffect(() => {
    const signal = isNewFromScratch ? "new" : tierProgramData ? "loaded" : null;
    if (signal === null || signal === contentHydrationRef.current) return;
    contentHydrationRef.current = signal;

    if (signal === "new") {
      setBannerUrl(null);
      form.setFieldsValue({ cards: [], faqs: [], rules: [] });
      return;
    }
    setBannerUrl(initialBannerUrl);
    form.setFieldsValue({ cards: initialCards, faqs: initialFaqs, rules: initialRules });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewFromScratch, tierProgramData]);

  const cardsWatch: WorkflowCard[] = Form.useWatch("cards", form) || initialCards;
  const faqsWatch: FaqItem[] = Form.useWatch("faqs", form) || initialFaqs;
  const rulesWatch: RuleTopic[] = Form.useWatch("rules", form) || initialRules;

  const [cardsExpanded, setCardsExpanded] = useState(true);
  const [faqsExpanded, setFaqsExpanded] = useState(true);
  const [rulesExpanded, setRulesExpanded] = useState(true);
  const tierColorOf = (tierKey: string) => config.tiers.find((t) => t.key === tierKey)?.color || color.Grey;

  const tierLevelsFromApi = tierProgramData?.tierLevels;

  const formatTierSubLabel = (min?: number, max?: number | null): string => {
    if (min === undefined || min === null) return "";
    const unit = config.volumeUnit;
    return max === null || max === undefined
      ? // "ตัน (MT)" ends in Latin, so it needs a space before ขึ้นไป; "ล้านบาท" reads fine without.
        `${min.toLocaleString("th-TH")} ${unit}${/[A-Za-z)]$/.test(unit) ? " " : ""}ขึ้นไป`
      : `${min.toLocaleString("th-TH")}-${max.toLocaleString("th-TH")} ${unit}`;
  };

  const tierDefs: TierDefinition[] =
    tierLevelsFromApi?.length > 0
      ? tierLevelsFromApi.map((t: any) => ({
          key: t.tierName,
          label: t.tierName,
          subLabel: formatTierSubLabel(t.minTarget, t.tierName === "Wisdom Plus" ? null : t.maxTarget),
          color: t.color || tierColorOf(t.tierName),
          img: config.tiers.find((c) => c.key === t.tierName)?.img,
        }))
      : config.tiers;

  const buildInitialTiers = (): EditableTierBenefit[] => {
    if (tierLevelsFromApi?.length > 0) {
      return tierLevelsFromApi.map((t: any) => ({
        tierKey: t.tierName,
        tierImageUrl: t.tierImageUrl,
        tierBgImageUrl: t.tierBgImageUrl,
        thresholdLabel: formatTierSubLabel(t.minTarget, t.maxTarget),
        tierTitle: t.rebateTitle,
        titleDescription: t.rebateDetail,
        percent: toRebatePercent(t.rebatePercent),
        showInApp: t.isShowRebatePercent,
        benefits: (t.tierLevelBenefits || []).map((b: any) => ({
          title: b.benefitName ?? "",
          description: b.benefitDetail ?? "",
          iconUrl: b.benefitImageLink,
          iconPath: b.benefitImageUrl,
        })),
        queueThresholdPercent: t.reviewTriggerValue,
        preventAutoDemote: t.isNotAutoDemotable,
        color: t.color || tierColorOf(t.tierName),
      }));
    }

    return (mode === "edit" || startFrom === "copy") && sourceTiers.length > 0
      ? sourceTiers.map((t, i) => ({
          tierKey: t.tierKey,
          thresholdLabel: t.thresholdLabel,
          tierImageUrl: t.tierImageUrl,
          tierBgImageUrl: t.tierBgImageUrl,
          tierTitle: t.tierTitle,
          titleDescription: t.titleDescription,
          percent: toRebatePercent(t.percent),
          showInApp: t.showInApp,
          benefits: t.benefits.map((b) => ({ title: b, description: b, iconUrl: icon.redeemReward })),
          queueThresholdPercent: 95,
          preventAutoDemote: i === sourceTiers.length - 1,
          color: tierColorOf(t.tierKey),
        }))
      : tierDefs.map((t, i) => ({
          tierKey: t.key,
          thresholdLabel: t.subLabel,
          percent: 0,
          showInApp: false,
          benefits: [],
          queueThresholdPercent: 95,
          preventAutoDemote: i === tierDefs.length - 1,
          color: t.color,
        }));
  };

  const buildInitialCardImageOverrides = (): Record<string, string> => {
    const map: Record<string, string> = {};
    (tierLevelsFromApi || []).forEach((t: any) => {
      const link = t.tierImageLink || t.defaultTierImageLink;
      if (link) map[t.tierName] = link;
    });
    return map;
  };

  const buildInitialCardBackgroundOverrides = (): Record<string, string> => {
    const map: Record<string, string> = {};
    (tierLevelsFromApi || []).forEach((t: any) => {
      const link = t.tierBgImageLink || t.defaultTierBgImageLink;
      if (link) map[t.tierName] = link;
    });
    return map;
  };

  const [tiers, setTiers] = useState<EditableTierBenefit[]>(buildInitialTiers);
  const [initialTiers, setInitialTiers] = useState<EditableTierBenefit[]>(buildInitialTiers);
  const [selectedTierKey, setSelectedTierKey] = useState<string>(tierDefs[0]?.key || "");
  const [cardImageOverrides, setCardImageOverrides] = useState<Record<string, string>>(buildInitialCardImageOverrides);
  const [initialCardImageOverrides, setInitialCardImageOverrides] = useState<Record<string, string>>(
    buildInitialCardImageOverrides,
  );
  const [cardImageOverridesFile, setCardImageOverridesFile] = useState<Record<string, File>>({});
  const [cardBackgroundOverrides, setCardBackgroundOverrides] = useState<Record<string, string>>(
    buildInitialCardBackgroundOverrides,
  );
    const [cardBackgroundOverridesFile, setCardBackgroundOverridesFile] = useState<Record<string, File>>({});
  const [initialCardBackgroundOverrides, setInitialCardBackgroundOverrides] = useState<Record<string, string>>(
    buildInitialCardBackgroundOverrides,
  );
  const [colorOverrides, setColorOverrides] = useState<Record<string, string>>({});
  // Last colour object from the picker per tier, used for the saturation check (see saturationPercent).
  const [pickerColors, setPickerColors] = useState<Record<string, PickerColor>>({});
  const [showErrors, setShowErrors] = useState(false);
  // `at` changes on every press so pressing "ถัดไป" again re-scrolls to the same field.
  const [scrollTarget, setScrollTarget] = useState<{ id: string; at: number } | null>(null);

  // Runs after the render that expanded the section / switched tier, so the field exists.
  useEffect(() => {
    if (!scrollTarget) return;
    // Wait one frame: switching tier / expanding a section changes the layout, and a smooth
    // scroll started before that settles stops short of the field.
    const frame = requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(`[data-field-id="${scrollTarget.id}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.querySelector<HTMLElement>("input, textarea, .ql-editor")?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [scrollTarget]);

  // Same "fire once per real transition" guard as the content-hydration effect above —
  // prevents a spurious re-render elsewhere from wiping out tier edits / uploaded images.
  const tiersHydrationRef = useRef<string | null>(null);
  useEffect(() => {
    const signal = isNewFromScratch ? "new" : tierLevelsFromApi?.length ? "loaded" : null;
    if (signal === null || signal === tiersHydrationRef.current) return;
    tiersHydrationRef.current = signal;

    if (signal === "new") {
      const empty = buildInitialTiers();
      setTiers(empty);
      setInitialTiers(empty);
      setCardImageOverrides({});
      setInitialCardImageOverrides({});
      // Default background per tier from config. Set as the initial value too, so it doesn't
      // count as an edit; it is display-only (no File), so nothing extra is uploaded on save.
      const defaultBackgrounds: Record<string, string> = {};
      config.tiers.forEach((t) => {
        if (t.bg) defaultBackgrounds[t.key] = t.bg;
      });
      setCardBackgroundOverrides(defaultBackgrounds);
      setInitialCardBackgroundOverrides(defaultBackgrounds);
      return;
    }
    const rebuilt = buildInitialTiers();
    setTiers(rebuilt);
    setInitialTiers(rebuilt);
    const rebuiltImages = buildInitialCardImageOverrides();
    setCardImageOverrides(rebuiltImages);
    setInitialCardImageOverrides(rebuiltImages);
    const rebuiltBackgrounds = buildInitialCardBackgroundOverrides();
    setCardBackgroundOverrides(rebuiltBackgrounds);
    setInitialCardBackgroundOverrides(rebuiltBackgrounds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewFromScratch, tierLevelsFromApi]);

  const selectedTierIndex = tierDefs.findIndex((t) => t.key === selectedTierKey);
  const selectedTierDef = tierDefs[selectedTierIndex];
  const nextTierDef = tierDefs[selectedTierIndex];
  const selectedTierBenefit = tiers.find((t) => t.tierKey === selectedTierKey);
  const originalColor = selectedTierDef?.color || color.primary;
  const selectedColor = colorOverrides[selectedTierKey] || originalColor;
  const isColorChanged = Boolean(colorOverrides[selectedTierKey]);

  const updateColor = (value: string) => {
    setTiers((prev) => prev.map((t) => (t.tierKey === selectedTierKey ? { ...t, color: value } : t)));
  };

  const resetColor = () => {
    setColorOverrides((prev) => {
      const next = { ...prev };
      delete next[selectedTierKey];
      return next;
    });
    setPickerColors((prev) => {
      const next = { ...prev };
      delete next[selectedTierKey];
      return next;
    });
    updateColor(originalColor);
  };

  // Compares what the user can actually change, normalised: the API sends null/undefined for
  // empty text but the inputs write "", so typing "a" and deleting it must compare equal.
  const tierSnapshot = (t?: EditableTierBenefit): string =>
    t
      ? JSON.stringify({
          tierTitle: t.tierTitle ?? "",
          titleDescription: t.titleDescription ?? "",
          percent: toRebatePercent(t.percent),
          showInApp: Boolean(t.showInApp),
          queueThresholdPercent: t.queueThresholdPercent ?? null,
          preventAutoDemote: Boolean(t.preventAutoDemote),
          color: (t.color ?? "").toLowerCase(),
          benefits: t.benefits.map((b) => ({
            title: b.title ?? "",
            description: b.description ?? "",
            iconUrl: b.iconUrl ?? "",
            hasNewFile: Boolean(b.File),
          })),
        })
      : "";

  const changedTierKeys = new Set<string>();
  tiers.forEach((t) => {
    const initial = initialTiers.find((x) => x.tierKey === t.tierKey);
    if (tierSnapshot(t) !== tierSnapshot(initial)) changedTierKeys.add(t.tierKey);
  });
  Object.keys(cardImageOverrides).forEach((k) => {
    if (cardImageOverrides[k] !== initialCardImageOverrides[k]) changedTierKeys.add(k);
  });
  Object.keys(cardBackgroundOverrides).forEach((k) => {
    if (cardBackgroundOverrides[k] !== initialCardBackgroundOverrides[k]) changedTierKeys.add(k);
  });
  // Colour is part of tierSnapshot (tiers[].color follows the picker and "คืนค่าสีเดิม"), so picking
  // the original colour again no longer counts as a change.
  const changedTierCount = changedTierKeys.size;


  const nextTierThreshold = nextTierDef ? tierMinThreshold(nextTierDef.subLabel) : 0;
  const queueThresholdPercent = selectedTierBenefit?.queueThresholdPercent ?? 95;
  const queueThresholdVolume = (nextTierThreshold * queueThresholdPercent) / 100;

  const updateQueueThresholdPercent = (value: number) => {
    setTiers((prev) => prev.map((t) => (t.tierKey === selectedTierKey ? { ...t, queueThresholdPercent: value } : t)));
  };

  const updatePreventAutoDemote = (value: boolean) => {
    setTiers((prev) => prev.map((t) => (t.tierKey === selectedTierKey ? { ...t, preventAutoDemote: value } : t)));
  };

  const updatePercent = (value: number) => {
    const percent = toRebatePercent(value);
    setTiers((prev) => prev.map((t) => (t.tierKey === selectedTierKey ? { ...t, percent } : t)));
  };

  const addBenefit = () => {
    setTiers((prev) =>
      prev.map((t) =>
        t.tierKey === selectedTierKey ? { ...t, benefits: [...t.benefits, { title: "", description: "" }] } : t,
      ),
    );
  };

  const updateBenefit = (idx: number, patch: Partial<TierBenefitItem>) => {
    setTiers((prev) =>
      prev.map((t) =>
        t.tierKey === selectedTierKey
          ? { ...t, benefits: t.benefits.map((b, i) => (i === idx ? { ...b, ...patch } : b)) }
          : t,
      ),
    );
  };

  const updateTitleDescription = (value: any): void => {
    setTiers((prev) =>
      prev.map((t) => (t.tierKey === selectedTierKey ? { ...t, titleDescription: value ?? "" } : t)),
    );
  };

  const updateTierTitle = (value: any): void => {
    setTiers((prev) =>
      prev.map((t) => (t.tierKey === selectedTierKey ? { ...t, tierTitle: value ?? "" } : t)),
    );
  };

  const removeBenefit = (idx: number) => {
    setTiers((prev) =>
      prev.map((t) => (t.tierKey === selectedTierKey ? { ...t, benefits: t.benefits.filter((_, i) => i !== idx) } : t)),
    );
  };

  const isCopyFromYear = mode === "create" && startFrom === "copy";
  const totalTierCount = tierDefs.length;
  const filledTierCount = tiers.filter(
    (t) => t.benefits.length > 0 && t.benefits.every((b) => b.title.trim() !== ""),
  ).length;

  let statusText: string;
  if (mode === "edit") {
    statusText = changedTierCount === 0 ? "ยังไม่มีการแก้ไข" : `แก้ไขแล้ว ${changedTierCount} ระดับ`;
  } else if (isCopyFromYear) {
    statusText =
      changedTierCount === 0 ? "ใช้ชุดที่คัดลอกมาทั้งหมด" : `ปรับจากชุดที่คัดลอกมา ${changedTierCount} ระดับ`;
  } else if (isNewFromScratch) {
    statusText =
      filledTierCount === 0 ? "ยังไม่ได้กรอกสิทธิประโยชน์" : `กรอกแล้ว ${filledTierCount} ระดับจาก ${totalTierCount}`;
  } else {
    statusText = changedTierCount === 0 ? "ยังไม่มีการแก้ไข" : "มีการแก้ไขที่ยังไม่บันทึก";
  }

  // Every missing/invalid field, in page order. Shown inline (red border + message) once the
  // user has pressed "ถัดไป"; that press also scrolls to the first entry.
  const fieldErrors: FieldErrorItem[] = [];
  const pushError = (item: FieldErrorItem, failed: boolean) => {
    if (failed) fieldErrors.push(item);
  };

  pushError({ id: "cards-empty", message: "กรุณาเพิ่มการ์ดอย่างน้อย 1 การ์ด", section: "cards" }, cardsWatch.length === 0);
  cardsWatch.forEach((c, i) => {
    pushError({ id: `card-${i}-icon`, message: "กรุณาอัปโหลดไอคอน", section: "cards" }, !c?.iconUrl && !c?.File);
    pushError({ id: `card-${i}-title`, message: "กรุณากรอกหัวข้อการ์ด", section: "cards" }, isBlankText(c?.title));
    pushError(
      { id: `card-${i}-description`, message: "กรุณากรอกรายละเอียด", section: "cards" },
      isBlankText(c?.description),
    );
  });

  pushError({ id: "faqs-empty", message: "กรุณาเพิ่มคำถามอย่างน้อย 1 คำถาม", section: "faqs" }, faqsWatch.length === 0);
  faqsWatch.forEach((f, i) => {
    pushError({ id: `faq-${i}-question`, message: "กรุณากรอกคำถาม", section: "faqs" }, isBlankText(f?.question));
    pushError({ id: `faq-${i}-answer`, message: "กรุณากรอกคำตอบ", section: "faqs" }, isBlankText(f?.answer));
  });

  pushError({ id: "rules-empty", message: "กรุณาเพิ่มหัวข้ออย่างน้อย 1 หัวข้อ", section: "rules" }, rulesWatch.length === 0);
  rulesWatch.forEach((r, i) => {
    pushError({ id: `rule-${i}-title`, message: "กรุณากรอกหัวข้อ", section: "rules" }, isBlankText(r?.title));
    pushError({ id: `rule-${i}-content`, message: "กรุณากรอกเนื้อหา", section: "rules" }, isBlankHtml(r?.content));
  });

  // Tiers in the order of the tier list on the left.
  tierDefs.forEach((def) => {
    const t = tiers.find((x) => x.tierKey === def.key);
    if (!t) return;
    const tierKey = t.tierKey;
    pushError(
      { id: `tier-${tierKey}-rebate-title`, message: "กรุณากรอกชื่อสิทธิประโยชน์", tierKey },
      isBlankText(t.tierTitle),
    );
    pushError(
      { id: `tier-${tierKey}-rebate-description`, message: "กรุณากรอกคำอธิบาย", tierKey },
      isBlankText(t.titleDescription),
    );
    t.benefits.forEach((b, j) => {
      pushError(
        { id: `tier-${tierKey}-benefit-${j}-icon`, message: "กรุณาอัปโหลดไอคอน", tierKey },
        !b.iconUrl && !b.File,
      );
      pushError({ id: `tier-${tierKey}-benefit-${j}-title`, message: "กรุณากรอกชื่อสิทธิประโยชน์", tierKey }, isBlankText(b.title));
      pushError(
        { id: `tier-${tierKey}-benefit-${j}-description`, message: "กรุณากรอกคำอธิบาย", tierKey },
        isBlankText(b.description),
      );
    });
    pushError(
      { id: `tier-${tierKey}-color`, message: "สีกลืนกับพื้นหลัง", tierKey },
      saturationPercent(t.color, pickerColors[tierKey]) < MIN_TIER_COLOR_SATURATION_PERCENT,
    );
  });

  const errorMessages: Record<string, string> = {};
  fieldErrors.forEach((e) => {
    errorMessages[e.id] = e.message;
  });
  const errorOf = (id: string): string | undefined => (showErrors ? errorMessages[id] : undefined);

  const handleNext = () => {
    if (fieldErrors.length > 0) {
      setShowErrors(true);
      const first = fieldErrors[0];
      // The target may sit in a collapsed section or in another tier than the one on screen.
      if (first.section === "cards") setCardsExpanded(true);
      if (first.section === "faqs") setFaqsExpanded(true);
      if (first.section === "rules") setRulesExpanded(true);
      if (first.tierKey && first.tierKey !== selectedTierKey) setSelectedTierKey(first.tierKey);
      setScrollTarget({ id: first.id, at: Date.now() });
      return;
    }
    onNext({
      initialBannerPath,
      bannerUrl,
      bannerFile: bannerFile!,
      cards: form.getFieldValue("cards") || initialCards,
      faqs: form.getFieldValue("faqs") || initialFaqs,
      rules: form.getFieldValue("rules") || initialRules,
      tiers,
      cardImageOverrides,
      cardBackgroundOverrides,
      cardImageOverridesFile,
      cardBackgroundOverridesFile,
      colorOverrides,
      changedTierKeys: Array.from(changedTierKeys),
    });
  };

  const beforeUploadBanner = createImageMaxSizeValidator(900, 883);
  const beforeUploadCardImage = createImageMaxSizeValidator(720, 405);
  const beforeUploadBackground = createImageMaxSizeValidator(853, 711);

  const bannerForCompany = () => {
    switch (company) {
      case "ICPF":
        return image.tierBannerFer;
      case "ICPI":
        return image.tierBannerInter;
      case "ICPL":
        return image.tierBanner;
      default:
        return image.tierBanner;
    }
  }

  // const uploadHowItWorksIcon = async (file: File): Promise<string> => {
  //   const res = await uploadContentImage(file, "HOW_IT_WORKS");
  //   return res?.responseData?.url;
  // };

  // const uploadHowItWorksBenefit = async (file: File): Promise<string> => {
  //   const res = await uploadContentImage(file, "BENEFIT");
  //   return res?.responseData?.url;
  // };

  return (
    <Form form={form} layout="vertical" disabled={readOnly} initialValues={{ cards: initialCards, faqs: initialFaqs, rules: initialRules }}>
      <div style={{ display: "flex", flexDirection: "column", marginTop: 24 }}>
        <div style={{ marginBottom: 24, background: color.background1, borderRadius: 8, padding: 16 }}>
          <Text fontFamily="Sarabun" fontWeight={600} fontSize={16} color="Text2">
            แบนเนอร์หน้าข้อมูลระดับ
          </Text>
          <div style={{ marginTop: 2, marginBottom: 12 }}>
            <Text fontFamily="Sarabun" fontSize={13} color="Text3">
              ภาพหัวข้อที่ร้านเห็นเมื่อกดดูข้อมูลระดับ ใช้ร่วมกันทุกระดับในปีนี้
            </Text>
          </div>
          <div style={{ display: "flex", alignItems: "start", gap: 16 }}>
            <div
              style={{
                width: 140,
                height: 140,
                borderRadius: 12,
                overflow: "hidden",
                border: `1px solid ${color.border}`,
                background: `linear-gradient(135deg, ${color.Text2} 0%, ${color.secondary} 140%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {bannerUrl ? (
                <img src={bannerUrl} alt="แบนเนอร์" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <img src={bannerForCompany()} alt="แบนเนอร์" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              )}
            </div>
            {!readOnly && <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "flex-start"}}>
              <ImgCrop aspect={900 / 883} showGrid modalTitle="ปรับขนาดแบนเนอร์" modalOk="ยืนยัน" modalCancel="ยกเลิก">
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={beforeUploadBanner}
                  customRequest={async ({ file, onSuccess }) => {
                    setBannerUrl(URL.createObjectURL(file as File));
                    setBannerFile(file as File);
                    onSuccess?.("ok");
                  }}
                >
                  <Button typeButton="border-primary" title="เปลี่ยนแบนเนอร์" style={{ width: "auto" }} />
                </Upload>
              </ImgCrop>
              <div style={{ marginTop: 8 }}>
                <Text fontFamily="Sarabun" fontSize={12} color="Text3">
                  ไฟล์ภาพ PNG ขนาดแนะนำ 900x883 พิกเซล
                </Text>
              </div>
            </div>}
          </div>
        </div>

        <CardSection
          style={{ background: color.background1 }}
          leftComponent={
            <SectionHeader title="วิธีสะสมยอด" subtitle="การ์ดอธิบายวิธีสะสมยอดและการเลื่อนระดับ ร้านเห็นในแท็บวิธีสะสมยอด · ไฟล์ไอคอน: PNG พื้นหลังโปร่งใส ขนาด 144x144 พิกเซล" />
          }
          extra={
            <CountToggle
              count={cardsWatch.length}
              unit="การ์ด"
              expanded={cardsExpanded}
              onToggle={() => setCardsExpanded((v) => !v)}
            />
          }
        >
          {cardsExpanded && (
            <div style={{ padding: "0 16px 16px",background: color.background1 }}>
              <Form.List name="cards">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <ItemRow key={key} onDelete={readOnly ? undefined : () => remove(name)}>
                        <div
                          data-field-id={errorOf(`card-${name}-icon`) ? `card-${name}-icon` : `card-${name}-title`}
                          style={{
                            border: `1px solid ${errorOf(`card-${name}-title`) ? color.error : color.border}`,
                            borderRadius: 8,
                            padding: "8px 12px",
                            marginBottom: 6,
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                          }}
                        >
                          <Form.Item {...restField} name={[name, "iconUrl"]} noStyle>
                            <IconUploadBox
                              disabled={readOnly}
                              hasError={!!errorOf(`card-${name}-icon`)}
                              onFileChange={async (file) => {
                                form.setFieldValue(["cards", name, "File"], file);
                              }}
                            />
                          </Form.Item>
                          <Form.Item {...restField} name={[name, "title"]} noStyle>
                            <Input placeholder="หัวข้อการ์ด" style={{ border: "none", boxShadow: "none", padding: 0, fontWeight: 700 }} />
                          </Form.Item>
                        </div>
                        <FieldError message={errorOf(`card-${name}-icon`)} />
                        <FieldError message={errorOf(`card-${name}-title`)} />
                        <div data-field-id={`card-${name}-description`} style={{ marginTop: 6 }}>
                          <Form.Item {...restField} name={[name, "description"]} noStyle>
                            <TextArea
                              placeholder="อธิบายวิธีสะสมยอด"
                              rows={2}
                              status={errorOf(`card-${name}-description`) ? "error" : undefined}
                            />
                          </Form.Item>
                          <FieldError message={errorOf(`card-${name}-description`)} />
                        </div>
                      </ItemRow>
                    ))}
                    <div data-field-id="cards-empty">
                      <FieldError message={errorOf("cards-empty")} />
                    </div>
                    {!readOnly && <Button
                      typeButton="border-dashed-primary"
                      icon={<PlusOutlined />}
                      title="เพิ่มการ์ด"
                      style={{ width: "auto" }}
                      onClick={() => add({ title: "", description: "" })}
                    />}
                  </>
                )}
              </Form.List>
            </div>
          )}
        </CardSection>

        <CardSection
          style={{ background: color.background1 }}
          leftComponent={
            <SectionHeader title="คำถามที่พบบ่อย" subtitle="ชุดคำถามของปีนี้ ร้านเห็นในแท็บคำถามที่พบบ่อยของหน้าข้อมูลระดับ" />
          }
          extra={
            <CountToggle
              count={faqsWatch.length}
              unit="คำถาม"
              expanded={faqsExpanded}
              onToggle={() => setFaqsExpanded((v) => !v)}
            />
          }
        >
          {faqsExpanded && (
            <div style={{ padding: "0 16px 16px", background: color.background1 }}>
              <Form.List name="faqs">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <ItemRow key={key} onDelete={readOnly ? undefined : () => remove(name)}>
                        <div data-field-id={`faq-${name}-question`} style={{ marginBottom: 6 }}>
                          <Form.Item {...restField} name={[name, "question"]} noStyle>
                            <Input placeholder="คำถาม" status={errorOf(`faq-${name}-question`) ? "error" : undefined} />
                          </Form.Item>
                          <FieldError message={errorOf(`faq-${name}-question`)} />
                        </div>
                        <div data-field-id={`faq-${name}-answer`}>
                          <Form.Item {...restField} name={[name, "answer"]} noStyle>
                            <TextArea
                              placeholder="คำตอบที่ร้านค้าจะเห็น"
                              rows={2}
                              status={errorOf(`faq-${name}-answer`) ? "error" : undefined}
                            />
                          </Form.Item>
                          <FieldError message={errorOf(`faq-${name}-answer`)} />
                        </div>
                      </ItemRow>
                    ))}
                    <div data-field-id="faqs-empty">
                      <FieldError message={errorOf("faqs-empty")} />
                    </div>
                    {!readOnly && <Button
                      typeButton="border-dashed-primary"
                      icon={<PlusOutlined />}
                      title="เพิ่มคำถาม"
                      style={{ width: "auto" }}
                      onClick={() => add({ question: "", answer: "" })}
                    />}
                  </>
                )}
              </Form.List>
            </div>
          )}
        </CardSection>

        <CardSection
          style={{ background: color.background1 }}
          leftComponent={<SectionHeader title="กติกาและเงื่อนไข" subtitle="หัวข้อและเนื้อหาของหน้ากติกาและเงื่อนไขในปีนี้" />}
          extra={
            <CountToggle
              count={rulesWatch.length}
              unit="หัวข้อ"
              expanded={rulesExpanded}
              onToggle={() => setRulesExpanded((v) => !v)}
            />
          }
        >
          {rulesExpanded && (
            <div style={{ padding: "0 16px 16px",background: color.background1 }}>
              <Form.List name="rules">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <div key={key} style={{ marginBottom: 16 }}>
                        <Row wrap={false} align="middle" gutter={8} style={{ marginBottom: 8 }}>
                          <Col flex="auto" data-field-id={`rule-${name}-title`}>
                            <Form.Item {...restField} name={[name, "title"]} noStyle>
                              <Input placeholder="หัวข้อ" status={errorOf(`rule-${name}-title`) ? "error" : undefined} />
                            </Form.Item>
                          </Col>
                          {!readOnly && <Col flex="none">
                            <div
                              onClick={() => remove(name)}
                              style={{
                                width: 40,
                                height: 40,
                                borderRadius: 8,
                                border: `1px solid ${color.error}`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                              }}
                            >
                              <DeleteOutlined style={{ color: color.error }} />
                            </div>
                          </Col>}
                        </Row>
                        {/* Title's message sits under the row so it doesn't push the delete button. */}
                        <div style={{ marginTop: -4, marginBottom: 8 }}>
                          <FieldError message={errorOf(`rule-${name}-title`)} />
                        </div>
                        <QuillContainer data-field-id={`rule-${name}-content`} $hasError={!!errorOf(`rule-${name}-content`)}>
                          <Form.Item {...restField} name={[name, "content"]} noStyle>
                            <ReactQuill
                              theme="snow"
                              modules={readOnly ? { toolbar: false } : quillModules}
                              readOnly={readOnly}
                              placeholder="เนื้อหาของหัวข้อนี้"
                              style={{ background: color.white }}
                            />
                          </Form.Item>
                        </QuillContainer>
                        <FieldError message={errorOf(`rule-${name}-content`)} />
                      </div>
                    ))}
                    <div data-field-id="rules-empty">
                      <FieldError message={errorOf("rules-empty")} />
                    </div>
                    {!readOnly && <Button
                      typeButton="border-dashed-primary"
                      icon={<PlusOutlined />}
                      title="เพิ่มหัวข้อ"
                      style={{ width: "auto" }}
                      onClick={() => add({ title: "", content: "" })}
                    />}
                  </>
                )}
              </Form.List>
            </div>
          )}
        </CardSection>

        <Text fontWeight={700} fontSize={16} color="Text2">
          รายละเอียดแต่ละระดับ
        </Text>
        <div style={{ display: "flex", gap: 24, marginTop: 12, marginBottom: 8 }}>
          <div style={{ width: 200, flexShrink: 0 }}>
            {tierDefs.map((t) => {
              const active = t.key === selectedTierKey;
              return (
                <div
                  key={t.key}
                  onClick={() => setSelectedTierKey(t.key)}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 8,
                    cursor: "pointer",
                    marginBottom: 4,
                    background: active ? color.background1 : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                >
                  <Text fontFamily="Sarabun" fontSize={16} fontWeight={active ? 700 : 400} color={active ? "primary" : "Text2"}>
                    {t.label}
                  </Text>
                  {/* Orange dot = this tier differs from what was loaded (same set the status text counts). */}
                  {changedTierKeys.has(t.key) && (
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: color.softWarning, flexShrink: 0 }} />
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <Text level={3} fontWeight={700} color="Text2">{selectedTierDef?.label}</Text>
            <div style={{ marginTop: 4, marginBottom: 20 }}>
              <Text fontSize={14} color="Text3">เกณฑ์ {selectedTierDef?.subLabel}</Text>
            </div>

            <Text fontWeight={700} fontSize={16}>รายการสิทธิประโยชน์</Text>
            <div style={{ marginTop: 12, marginBottom: 24 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                  padding: "8px 0px",
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    background: "#FFF",
                    color: color.background4,
                    border: `1px solid ${color.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <PercentageOutlined />
                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                  <div data-field-id={`tier-${selectedTierKey}-rebate-title`}>
                    <Input
                      value={selectedTierBenefit?.tierTitle ?? ""}
                      onChange={(e) => updateTierTitle(e.target.value)}
                      placeholder="ชื่อสิทธิประโยชน์"
                      status={errorOf(`tier-${selectedTierKey}-rebate-title`) ? "error" : undefined}
                    />
                    <FieldError message={errorOf(`tier-${selectedTierKey}-rebate-title`)} />
                  </div>
                  <div data-field-id={`tier-${selectedTierKey}-rebate-description`}>
                    <Input
                      value={selectedTierBenefit?.titleDescription ?? ""}
                      onChange={(e) => updateTitleDescription(e.target.value)}
                      placeholder="คำอธิบายใต้ชื่อ"
                      status={errorOf(`tier-${selectedTierKey}-rebate-description`) ? "error" : undefined}
                    />
                    <FieldError message={errorOf(`tier-${selectedTierKey}-rebate-description`)} />
                  </div>
                  <Input
                    type="number"
                    min={0}
                    max={MAX_REBATE_PERCENT}
                    value={toRebatePercent(selectedTierBenefit?.percent)}
                    onChange={(e) => {
                      const value = parseCappedNumber(e.target.value, MAX_REBATE_PERCENT);
                      if (value !== null) updatePercent(value);
                    }}
                    onKeyDown={blockSignAndExponent}
                    suffix="%"
                  />
                </div>
                <div style={{ width : 44 }} />
              </div>

              {selectedTierBenefit?.benefits.map((b, idx) => (
                <div key={idx} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 8 }}>
                  <div data-field-id={`tier-${selectedTierKey}-benefit-${idx}-icon`}>
                    <IconUploadBox
                      disabled={readOnly}
                      value={b.iconUrl}
                      hasError={!!errorOf(`tier-${selectedTierKey}-benefit-${idx}-icon`)}
                      onChange={(v) => updateBenefit(idx, { iconUrl: v })}
                      onFileChange={async (file) => {
                        updateBenefit(idx, { File: file })
                      }}
                    />
                  </div>
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                    <div data-field-id={`tier-${selectedTierKey}-benefit-${idx}-title`}>
                      <Input
                        value={b.title}
                        onChange={(e) => updateBenefit(idx, { title: e.target.value })}
                        placeholder="ชื่อสิทธิประโยชน์"
                        status={errorOf(`tier-${selectedTierKey}-benefit-${idx}-title`) ? "error" : undefined}
                      />
                      <FieldError message={errorOf(`tier-${selectedTierKey}-benefit-${idx}-title`)} />
                      {/* The icon box is only 40px wide, so its message sits under the name's. */}
                      <FieldError message={errorOf(`tier-${selectedTierKey}-benefit-${idx}-icon`)} />
                    </div>
                    <div data-field-id={`tier-${selectedTierKey}-benefit-${idx}-description`}>
                      <Input
                        value={b.description}
                        onChange={(e) => updateBenefit(idx, { description: e.target.value })}
                        placeholder="คำอธิบายใต้ชื่อ"
                        status={errorOf(`tier-${selectedTierKey}-benefit-${idx}-description`) ? "error" : undefined}
                      />
                      <FieldError message={errorOf(`tier-${selectedTierKey}-benefit-${idx}-description`)} />
                    </div>
                  </div>
                  {!readOnly && <div
                    onClick={() => removeBenefit(idx)}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      border: `1px solid ${color.error}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  >
                    <img src={icon.delete} style={{ width: 15, height: 16 }} alt="Delete" />
                  </div>}
                </div>
              ))}

              {!readOnly && <>
                <Button
                  typeButton="border-dashed-primary"
                  icon={<PlusOutlined />}
                  title="เพิ่มสิทธิประโยชน์"
                  style={{ width: "auto" }}
                  onClick={addBenefit}
                />
                <div style={{ marginTop: 8 }}>
                  <Text fontSize={12} color="Text3">ไฟล์ไอคอน: PNG พื้นหลังโปร่งใส ขนาด 144x144 พิกเซล</Text>
                </div>
              </>}
            </div>

            <Text fontWeight={700} fontSize={16}>รูปการ์ดระดับ</Text>
            <div style={{ display: "flex", gap: 16, alignItems: "flex-start", marginTop: 8, marginBottom: 24 }}>
              <TierCardPreview tier={selectedTierDef} imageOverride={cardImageOverrides[selectedTierKey]} width={180} />
              {!readOnly && <div>
                <ImgCrop aspect={720 / 405} showGrid modalTitle="ปรับขนาดรูปการ์ดระดับ" modalOk="ยืนยัน" modalCancel="ยกเลิก">
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={beforeUploadCardImage}
                    customRequest={({ file, onSuccess }) => {
                      setCardImageOverrides((prev) => ({ ...prev, [selectedTierKey]: URL.createObjectURL(file as File) }));
                      setCardImageOverridesFile((prev) => ({ ...prev, [selectedTierKey]: file as File }));
                      onSuccess?.("ok");
                    }}
                  >
                    <Button typeButton="border-primary" title="เปลี่ยนรูป" style={{ width: "auto" }} />
                  </Upload>
                </ImgCrop>
                <div style={{ marginTop: 8 }}>
                  <Text fontSize={12} color="Text3">ไฟล์ภาพสัดส่วน 16:9 ขนาดแนะนำ 720x405 พิกเซล</Text>
                </div>
              </div>}
            </div>

            <Text fontWeight={700} fontSize={16}>พื้นหลังของระดับ</Text>
            <div style={{ display: "flex", gap: 16, alignItems: "flex-start", marginTop: 8, marginBottom: 24 }}>
              <div
                style={{
                  width: 100,
                  height: 100,
                  borderRadius: 12,
                  border: `1px solid ${color.border}`,
                  overflow: "hidden",
                  background: color.background1,
                  flexShrink: 0,
                }}
              >
                {cardBackgroundOverrides[selectedTierKey] && (
                  <img
                    src={cardBackgroundOverrides[selectedTierKey]}
                    alt="พื้นหลังของระดับ"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                )}
              </div>
              {!readOnly && <div>
                <ImgCrop aspect={853 / 711} showGrid modalTitle="ปรับขนาดพื้นหลังของระดับ" modalOk="ยืนยัน" modalCancel="ยกเลิก">
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={beforeUploadBackground}
                    customRequest={({ file, onSuccess }) => {
                      setCardBackgroundOverrides((prev) => ({ ...prev, [selectedTierKey]: URL.createObjectURL(file as File) }));
                      setCardBackgroundOverridesFile((prev) => ({ ...prev, [selectedTierKey]: file as File }));
                      onSuccess?.("ok");
                    }}
                  >
                    <Button typeButton="border-primary" title="เปลี่ยนพื้นหลัง" style={{ width: "auto" }} />
                  </Upload>
                </ImgCrop>
                <div style={{ marginTop: 8 }}>
                  <Text fontSize={12} color="Text3">ไฟล์ภาพแนวตั้ง ขนาดแนะนำ 853x711 พิกเซล</Text>
                </div>
              </div>}
            </div>

            <Text fontWeight={700} fontSize={16}>สีของระดับ</Text>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginTop: 8, marginBottom: 24 }}>
              <ColorPicker
                // Keep the picker's own colour object so its HSB fields don't jump after the hex round-trip.
                value={pickerColors[selectedTierKey] ?? selectedColor}
                size='large'
                disabled={readOnly}
                defaultFormat="hex"
                showText
                onChange={(value) => {
                  setPickerColors((prev) => ({ ...prev, [selectedTierKey]: value }));
                  const hex = value.toHexString();
                  setColorOverrides((prev) => ({ ...prev, [selectedTierKey]: hex }));
                  updateColor(hex);
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: selectedColor,
                    border: `1px solid ${color.border}`,
                    flexShrink: 0,
                    marginTop: 2,
                    cursor: readOnly ? "default" : "pointer",
                  }}
                />
              </ColorPicker>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Text fontWeight={700} fontSize={14} color="Text2">{selectedColor.toUpperCase()}</Text>
                </div>
                {!readOnly && isColorChanged && (
                    <div
                      style={{ width: "auto", padding: "10px 0px", cursor: "pointer" }}
                      onClick={resetColor}
                    >
                      <Text fontWeight={500} fontSize={14} color="primary">คืนค่าสีเดิม</Text>
                    </div>
                  )}
                <div style={{ marginTop: 4 }}>
                  <Text fontSize={12} color="Text3">
                    ใช้กับทุกที่ที่แสดงระดับนี้ ทั้งป้ายในตาราง การ์ดนับร้าน และในแอปร้านค้า
                  </Text>
                </div>
                {saturationPercent(selectedColor, pickerColors[selectedTierKey]) < MIN_TIER_COLOR_SATURATION_PERCENT && (
                  <div style={{ marginTop: 4 }} data-field-id={`tier-${selectedTierKey}-color`}>
                    <Text fontFamily="Sarabun" fontSize={14} color="error">
                      สีกลืนกับพื้นหลัง
                    </Text>
                  </div>
                )}
              </div>
            </div>
            {
              (selectedTierKey !== "Follow" && selectedTierKey !== "Basic") && (<>
                <Text fontWeight={700} fontSize={16}>เงื่อนไขการพิจารณาระดับ</Text>
                <div
                  style={{
                    border: `1px solid ${color.border}`,
                    borderRadius: 8,
                    padding: 16,
                    marginTop: 8,
                    background: isClosingReviewOpen ? color.background1 : undefined,
                  }}
                >
                  {nextTierDef ? (
                    <>
                      {isClosingReviewOpen && (
                        <ClosingReviewNotice startDate={closingReview?.startDate} endDate={closingReview?.endDate} />
                      )}
                      <Text fontWeight={700} fontSize={14} color="Text2">เกณฑ์เข้าคิวให้ผู้จัดการพิจารณาเลื่อนขั้น</Text>
                      <div style={{ marginTop: 2, marginBottom: 12 }}>
                        <Text fontSize={13} color="Text3">
                          ร้านที่ทำยอดได้ตาม % ของเกณฑ์ระดับ {nextTierDef.label} จะเข้าคิวพิจารณาเลื่อนขั้น ให้ผู้จัดการตัดสินในรอบรีวิวสิ้นปี
                        </Text>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                        <Input
                          type="number"
                          min={0}
                          max={MAX_QUEUE_THRESHOLD_PERCENT}
                          value={queueThresholdPercent}
                          onChange={(e) => {
                            const value = parseCappedNumber(e.target.value, MAX_QUEUE_THRESHOLD_PERCENT);
                            if (value !== null) updateQueueThresholdPercent(value);
                          }}
                          onKeyDown={blockSignAndExponent}
                          disabled={isClosingReviewOpen}
                          suffix="%"
                          style={{ width: 160 }}
                        />
                        <Text fontSize={13} color="Text3">
                          = {formatVolumeAmount(queueThresholdVolume, config.volumeUnit)} {config.volumeUnit} จากเกณฑ์เต็ม {formatVolumeAmount(nextTierThreshold, config.volumeUnit)} {config.volumeUnit}
                        </Text>
                      </div>
                      { config.company != "ICPL" && <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                        <Switch
                          checked={selectedTierBenefit?.preventAutoDemote ?? false}
                          onChange={updatePreventAutoDemote}
                          disabled={isClosingReviewOpen}
                        />
                        <div>
                          <Text fontWeight={700} fontSize={14} color="Text2">ห้ามระบบลดระดับนี้อัตโนมัติ</Text>
                          <div style={{ marginTop: 2 }}>
                            <Text fontSize={12} color="Text3">
                              สิ้นปีร้านที่ยอดไม่ถึงจะเข้าคิวให้ผู้จัดการรีวิวก่อน ระบบจะไม่ลดระดับอัตโนมัติ
                            </Text>
                          </div>
                        </div>
                      </div>}
                    </>
                  ) : (
                    <Text fontSize={13} color="Text3">ระดับนี้เป็นระดับสูงสุดแล้ว ไม่มีระดับถัดไปให้พิจารณาเลื่อนขั้น</Text>
                  )}
                </div>
              </>)
            }
          </div>

          <div style={{ width: 280, background: color.background1, borderRadius: 8, padding: 16, alignSelf: "flex-start", height: "fit-content" }}>
            <div style={{ flexShrink: 0, borderRadius: 8, padding: 16, background: color.white, display: "flex", flexDirection: "column", gap: 8 }}>
              <Text fontSize={13} color="Text3">ตัวอย่างที่ร้านค้าเห็น</Text>
              <div style={{ marginTop: 8 }}>
                <TierCardPreview tier={selectedTierDef} imageOverride={cardImageOverrides[selectedTierKey]} width="100%" />
              </div>
              <div style={{ marginTop: 12 }}>
                <Text fontWeight={700} fontSize={14} color="Text2">สิทธิประโยชน์ของระดับ {selectedTierDef?.label}</Text>
              </div>
              <Tag style={{ borderRadius: "16px", width: "fit-content" }} color={selectedColor}>
                <Text fontFamily="Sarabun" fontSize={14} fontWeight={700} color="white">
                  {selectedTierDef?.label || ""}
                </Text>
              </Tag>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, borderWidth: 1, borderStyle: "solid", borderColor: color.border, borderRadius: 8, padding: "8px 12px" }}>
                <div>
                  <Text fontWeight={700} fontSize={13} color="Text2">{selectedTierBenefit?.tierTitle }</Text>
                  <div>
                    <Text fontSize={12} color="Text3">{selectedTierBenefit?.titleDescription}</Text>
                  </div>
                </div>
                <Text fontSize={20} fontWeight={700} color="secondary3">{toRebatePercent(selectedTierBenefit?.percent) + "%"}</Text>
              </div>
              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                {selectedTierBenefit && selectedTierBenefit.benefits.length > 0 ? (
                  selectedTierBenefit.benefits.filter((item) => item.title.trim() !== "").map((b, idx) => (
                    <div key={idx} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, borderWidth: 1, borderStyle: "solid", borderColor: color.border, borderRadius: 8, padding: "8px 12px" }}>
                      <div>
                        <Text fontWeight={700} fontSize={13} color="Text2">{b.title || "สิทธิประโยชน์"}</Text>
                        {b.description && (
                          <div>
                            <Text fontSize={12} color="Text3">{b.description}</Text>
                          </div>
                        )}
                      </div>
                      {b.iconUrl && (
                        <img src={b.iconUrl} alt="" style={{ width: 30, height: 30, objectFit: "contain", flexShrink: 0 }} />
                      )}
                    </div>
                  ))
                ) : (
                  <Text fontSize={13} color="Text3">ยังไม่มีสิทธิประโยชน์ในระดับนี้</Text>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* {hasLowSaturationColor && (
          <div style={{ marginBottom: 8 }}>
            <Text fontSize={13} color="error">
              สีของระดับ {lowSaturationTierLabels.join(", ")} กลืนกับพื้นหลัง กรุณาเลือกสีอื่นก่อนไปต่อ
            </Text>
          </div>
        )} */}
        <Divider />
        <Row justify="space-between" align="middle">
          <Col>
            {/* Explicit disabled={false}: the read-only Form disables every control inside it, this one must stay clickable. */}
            <Button
              typeButton="border-primary"
              title={readOnly ? "ปิด" : "ย้อนกลับ"}
              style={{ width: "auto" }}
              disabled={false}
              onClick={onBack}
            />
          </Col>
          {!readOnly && <Col>
            <Row align="middle" gutter={12}>
              <Col>
                <Text fontSize={13} color="Text3">{statusText}</Text>
              </Col>
              <Col>
                <Button
                  typeButton="primary"
                  title="ถัดไป: ตรวจสอบ"
                  style={{ width: "auto" }}
                  onClick={handleNext}
                />
              </Col>
            </Row>
          </Col>}
        </Row>
      </div>
    </Form>
  );
}

export default Step2;
